import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Upload, Link as LinkIcon, FileSpreadsheet, Download, Loader2,
  CheckCircle2, XCircle, AlertCircle, ChevronDown, ChevronUp, RefreshCw,
  FileText, Table, Trash2, Eye, Play, Copy, Zap, Info, MapPin
} from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const API = `${window.location.origin}/api`;
const POLL_INTERVAL = 3000; // 3 seconds

// ─── Status Badge ──────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const configs = {
    processing: { bg: 'bg-blue-500/20', text: 'text-blue-400', icon: Loader2, animate: true, label: 'Processing' },
    completed:  { bg: 'bg-emerald-500/20', text: 'text-emerald-400', icon: CheckCircle2, animate: false, label: 'Completed' },
    failed:     { bg: 'bg-red-500/20', text: 'text-red-400', icon: XCircle, animate: false, label: 'Failed' },
  };
  const config = configs[status] || configs.processing;
  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
      <Icon className={`w-3.5 h-3.5 ${config.animate ? 'animate-spin' : ''}`} />
      {config.label}
    </span>
  );
}

// ─── Preview Row ───────────────────────────────────────────────────────────────
function PreviewRow({ row }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className={`border rounded-lg overflow-hidden ${
      !row.is_valid ? 'border-red-500/30 bg-red-500/5' : 'border-zinc-800 bg-zinc-900/50'
    }`}>
      <div className="p-3 flex items-center justify-between cursor-pointer hover:bg-zinc-800/30" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center gap-3 min-w-0">
          <span className="text-xs text-zinc-500 font-mono w-8">#{row.row}</span>
          <span className="text-white font-medium truncate">{row.cleaned?.name || row.original?.name || 'Unknown'}</span>
          {row.cleaned?.category && <span className="text-xs text-zinc-500 hidden sm:inline">{row.cleaned.category}</span>}
        </div>
        <div className="flex items-center gap-2">
          {!row.is_valid
            ? <span className="text-xs text-red-400 flex items-center gap-1"><XCircle className="w-3.5 h-3.5" />Invalid</span>
            : <span className="text-xs text-emerald-400 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" />Ready</span>
          }
          {expanded ? <ChevronUp className="w-4 h-4 text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-zinc-500" />}
        </div>
      </div>
      <AnimatePresence>
        {expanded && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="border-t border-zinc-800">
            <div className="p-4 space-y-3">
              {row.errors?.length > 0 && (
                <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30">
                  <p className="text-sm font-medium text-red-400 mb-1">Errors:</p>
                  <ul className="text-xs text-red-300 list-disc list-inside">{row.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
                </div>
              )}
              {row.cleaned && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                  {Object.entries(row.cleaned).filter(([k, v]) => v && k !== 'social_links').map(([key, value]) => (
                    <div key={key}>
                      <span className="text-zinc-500 text-xs">{key}:</span>
                      <p className="text-white truncate">{String(value)}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Geocode Button ────────────────────────────────────────────────────────────
function GeocodeButton() {
  const [loading, setLoading] = useState(false);
  const handleGeocode = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/businesses/geocode-batch`, { method: 'POST', credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Geocoding failed');
      }
    } catch { toast.error('Error starting geocoding'); }
    finally { setLoading(false); }
  };
  return (
    <button onClick={handleGeocode} disabled={loading}
      className="flex items-center gap-2 px-3 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-300 hover:text-white text-sm transition-colors disabled:opacity-50"
      data-testid="geocode-btn" title="Add map coordinates to imported businesses (enables 'nearest first' sorting)">
      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
      Geocode Addresses
    </button>
  );
}

// ─── Import Job Card (with live progress) ─────────────────────────────────────
function ImportJobCard({ job: initialJob, onRefresh, onDelete }) {
  const [job, setJob] = useState(initialJob);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const pollRef = useRef(null);

  // Auto-poll while processing
  useEffect(() => {
    setJob(initialJob);
    if (initialJob.status === 'processing') {
      pollRef.current = setInterval(async () => {
        try {
          const res = await fetch(`${API}/admin/bulk-import/jobs/${initialJob.job_id}`, { credentials: 'include' });
          if (res.ok) {
            const updated = await res.json();
            setJob(updated);
            if (updated.status !== 'processing') {
              clearInterval(pollRef.current);
              onRefresh?.();
            }
          }
        } catch {}
      }, POLL_INTERVAL);
    }
    return () => clearInterval(pollRef.current);
  }, [initialJob.job_id, initialJob.status]);

  const handleDelete = async (deleteBusinesses) => {
    setDeleting(true);
    try {
      const res = await fetch(`${API}/admin/bulk-import/jobs/${job.job_id}?delete_businesses=${deleteBusinesses}`, {
        method: 'DELETE', credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        onDelete?.(job.job_id);
      } else {
        toast.error('Failed to delete job');
      }
    } catch { toast.error('Error deleting job'); }
    finally { setDeleting(false); setShowDeleteDialog(false); }
  };

  const progress = job.total_rows > 0 ? Math.round((job.processed_rows / job.total_rows) * 100) : 0;
  const eta = job.status === 'processing' && job.processed_rows > 0
    ? Math.round(((job.total_rows - job.processed_rows) / job.processed_rows) * ((Date.now() - new Date(job.created_at).getTime()) / 1000))
    : null;

  return (
    <>
      <div className="border border-zinc-800 rounded-xl p-4 bg-zinc-900/50">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-white font-medium truncate max-w-xs text-sm">{job.source_name || job.job_id}</h3>
              <StatusBadge status={job.status} />
            </div>
            <p className="text-xs text-zinc-500">{new Date(job.created_at).toLocaleString()}</p>
          </div>
          <button onClick={() => setShowDeleteDialog(true)} className="p-2 rounded-lg hover:bg-zinc-800 text-zinc-500 hover:text-red-400 transition-colors flex-shrink-0" data-testid={`delete-job-${job.job_id}`}>
            <Trash2 className="w-4 h-4" />
          </button>
        </div>

        {/* Live progress bar */}
        {(job.status === 'processing' || job.status === 'completed') && (
          <div className="mb-3">
            <div className="flex justify-between text-xs text-zinc-400 mb-1.5">
              <span>{job.status === 'processing' ? 'Importing...' : 'Complete'}</span>
              <span>{job.processed_rows.toLocaleString()} / {job.total_rows.toLocaleString()} rows {job.status === 'processing' && `(${progress}%)`}</span>
            </div>
            <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
              <motion.div
                className={`h-full rounded-full ${job.status === 'completed' ? 'bg-emerald-500' : 'bg-blue-500'}`}
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.5 }}
              />
            </div>
            {eta && eta > 0 && job.status === 'processing' && (
              <p className="text-xs text-zinc-500 mt-1">Estimated time remaining: ~{eta < 60 ? `${eta}s` : `${Math.round(eta / 60)}m`}</p>
            )}
          </div>
        )}

        {job.status === 'failed' && job.error_message && (
          <div className="mb-3 p-3 rounded-lg bg-red-500/10 border border-red-500/30">
            <p className="text-xs text-red-400">{job.error_message}</p>
          </div>
        )}

        <div className="grid grid-cols-4 gap-2 text-center">
          {[
            { label: 'Total', value: job.total_rows.toLocaleString(), color: 'text-white', bg: 'bg-zinc-800/50' },
            { label: 'Imported', value: job.imported_count.toLocaleString(), color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
            { label: 'Skipped', value: job.skipped_duplicates.toLocaleString(), color: 'text-yellow-400', bg: 'bg-yellow-500/10' },
            { label: 'Errors', value: job.error_count.toLocaleString(), color: 'text-red-400', bg: 'bg-red-500/10' },
          ].map(({ label, value, color, bg }) => (
            <div key={label} className={`p-2 rounded-lg ${bg}`}>
              <p className={`text-base font-bold ${color}`}>{value}</p>
              <p className="text-xs text-zinc-500">{label}</p>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-white">Delete Import Job</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Also delete the {job.imported_count.toLocaleString()} businesses imported by this job?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2">
            <button onClick={() => handleDelete(false)} disabled={deleting} className="px-4 py-2 rounded-lg bg-zinc-800 text-white hover:bg-zinc-700 transition-colors">Keep Businesses</button>
            <button onClick={() => handleDelete(true)} disabled={deleting} className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors flex items-center gap-2">
              {deleting && <Loader2 className="w-4 h-4 animate-spin" />}Delete All
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────
export default function AdminBulkImport() {
  const [activeTab, setActiveTab] = useState('upload');
  const [loading, setLoading] = useState(false);
  const [template, setTemplate] = useState(null);
  const [uploadMethod, setUploadMethod] = useState('file');
  const [file, setFile] = useState(null);
  const [googleSheetUrl, setGoogleSheetUrl] = useState('');
  const [preview, setPreview] = useState(null);
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);

  useEffect(() => {
    fetchTemplate();
    fetchJobs();
  }, []);

  const fetchTemplate = async () => {
    try {
      const res = await fetch(`${API}/admin/bulk-import/template`, { credentials: 'include' });
      if (res.ok) setTemplate(await res.json());
    } catch {}
  };

  const fetchJobs = useCallback(async () => {
    setLoadingJobs(true);
    try {
      const res = await fetch(`${API}/admin/bulk-import/jobs`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setJobs(data.jobs);
      }
    } catch {}
    finally { setLoadingJobs(false); }
  }, []);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const valid = ['.csv', '.xlsx', '.xls'].some(ext => f.name.toLowerCase().endsWith(ext));
    if (!valid) { toast.error('Please upload a CSV or Excel file'); return; }
    setFile(f);
    setPreview(null);
  };

  const handlePreview = async () => {
    if (uploadMethod === 'file' && !file) { toast.error('Please select a file first'); return; }
    if (uploadMethod === 'google' && !googleSheetUrl) { toast.error('Please enter a Google Sheets URL'); return; }
    setLoading(true);
    setPreview(null);
    try {
      const formData = new FormData();
      if (uploadMethod === 'file') formData.append('file', file);
      else formData.append('google_sheet_url', googleSheetUrl);
      const res = await fetch(`${API}/admin/bulk-import/preview`, { method: 'POST', credentials: 'include', body: formData });
      if (res.ok) {
        const data = await res.json();
        setPreview(data);
        setActiveTab('preview');
        toast.success(`Found ${data.total_rows.toLocaleString()} rows — showing first 100`);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to preview data');
      }
    } catch { toast.error('Error previewing data'); }
    finally { setLoading(false); }
  };

  const handleImport = async (skipPreview = false) => {
    if (!skipPreview && !preview) { toast.error('Please preview data first'); return; }
    if (uploadMethod === 'file' && !file) { toast.error('Please select a file first'); return; }
    if (uploadMethod === 'google' && !googleSheetUrl) { toast.error('Please enter a Google Sheets URL'); return; }
    setLoading(true);
    try {
      const formData = new FormData();
      if (uploadMethod === 'file') formData.append('file', file);
      else formData.append('google_sheet_url', googleSheetUrl);
      formData.append('skip_duplicates', skipDuplicates);
      const res = await fetch(`${API}/admin/bulk-import/execute`, { method: 'POST', credentials: 'include', body: formData });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Import started for ${data.total_rows.toLocaleString()} businesses!`);
        setFile(null);
        setGoogleSheetUrl('');
        setPreview(null);
        setActiveTab('history');
        await fetchJobs();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Import failed');
      }
    } catch { toast.error('Error starting import'); }
    finally { setLoading(false); }
  };

  const handleJobDelete = (jobId) => setJobs(prev => prev.filter(j => j.job_id !== jobId));

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 py-8 pt-24">

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white mb-1">Bulk Business Import</h1>
          <p className="text-zinc-400">Import thousands of businesses from CSV, Excel, or Google Sheets</p>
        </div>

        {/* Large import callout */}
        <div className="mb-6 p-4 rounded-xl bg-amber-500/10 border border-amber-500/30 flex gap-3">
          <Zap className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-semibold text-amber-300 mb-1">Handles 50,000+ businesses</p>
            <p className="text-amber-200/70">
              Imports run in the background — you can close this page after starting.
              For Google Sheets with 50k+ rows, the sheet must be set to <strong>"Anyone with the link can view"</strong>.
              You can also export your sheet as CSV (File → Download → CSV) and upload the file directly for fastest results.
            </p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-zinc-900 border border-zinc-800 mb-6">
            <TabsTrigger value="upload" className="data-[state=active]:bg-zinc-800">
              <Upload className="w-4 h-4 mr-2" />Upload
            </TabsTrigger>
            <TabsTrigger value="preview" className="data-[state=active]:bg-zinc-800" disabled={!preview}>
              <Eye className="w-4 h-4 mr-2" />Preview
              {preview && <span className="ml-2 px-1.5 py-0.5 text-xs bg-zinc-700 rounded">{preview.total_rows.toLocaleString()}</span>}
            </TabsTrigger>
            <TabsTrigger value="history" className="data-[state=active]:bg-zinc-800">
              <Table className="w-4 h-4 mr-2" />History
              {jobs.filter(j => j.status === 'processing').length > 0 && (
                <span className="ml-2 w-2 h-2 rounded-full bg-blue-500 animate-pulse inline-block" />
              )}
            </TabsTrigger>
          </TabsList>

          {/* ── Upload Tab ── */}
          <TabsContent value="upload" className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              {[
                { key: 'file', icon: FileSpreadsheet, label: 'Upload File', sub: 'CSV or Excel (.xlsx)' },
                { key: 'google', icon: LinkIcon, label: 'Google Sheets', sub: 'Paste public URL' },
              ].map(({ key, icon: Icon, label, sub }) => (
                <button key={key} onClick={() => setUploadMethod(key)} data-testid={`upload-${key}-btn`}
                  className={`p-4 rounded-xl border-2 transition-all ${uploadMethod === key ? 'border-amber-500 bg-amber-500/10' : 'border-zinc-800 bg-zinc-900/50 hover:border-zinc-700'}`}>
                  <Icon className={`w-8 h-8 mx-auto mb-2 ${uploadMethod === key ? 'text-amber-400' : 'text-zinc-500'}`} />
                  <p className="font-medium text-white">{label}</p>
                  <p className="text-xs text-zinc-500">{sub}</p>
                </button>
              ))}
            </div>

            {uploadMethod === 'file' && (
              <div className="border-2 border-dashed border-zinc-800 rounded-xl p-8 text-center">
                <input type="file" accept=".csv,.xlsx,.xls" onChange={handleFileChange} className="hidden" id="file-upload" data-testid="file-input" />
                <label htmlFor="file-upload" className="cursor-pointer block">
                  {file ? (
                    <div className="space-y-2">
                      <FileText className="w-12 h-12 mx-auto text-amber-400" />
                      <p className="text-white font-medium">{file.name}</p>
                      <p className="text-xs text-zinc-500">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                      <p className="text-xs text-zinc-400">Click to change file</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Upload className="w-12 h-12 mx-auto text-zinc-600" />
                      <p className="text-zinc-400">Click to upload or drag and drop</p>
                      <p className="text-xs text-zinc-600">CSV or Excel (.xlsx) — no size limit</p>
                    </div>
                  )}
                </label>
              </div>
            )}

            {uploadMethod === 'google' && (
              <div className="space-y-3">
                <Label className="text-zinc-400">Google Sheets URL</Label>
                <Input value={googleSheetUrl} onChange={e => setGoogleSheetUrl(e.target.value)}
                  placeholder="https://docs.google.com/spreadsheets/d/..."
                  className="bg-zinc-900 border-zinc-800 text-white" data-testid="google-sheet-input" />
                <div className="flex items-start gap-2 text-xs text-zinc-500">
                  <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>Sheet must be set to <strong className="text-zinc-300">Share → Anyone with the link → Viewer</strong>. For 50k+ rows, download as CSV and upload the file instead for faster results.</span>
                </div>
              </div>
            )}

            {/* Skip duplicates toggle */}
            <div className="flex items-center gap-3 p-4 rounded-xl bg-zinc-900 border border-zinc-800">
              <Switch checked={skipDuplicates} onCheckedChange={setSkipDuplicates} data-testid="skip-duplicates-toggle" />
              <div>
                <Label className="text-white cursor-pointer">Skip Duplicate Businesses</Label>
                <p className="text-xs text-zinc-500">Businesses with the same name + city will be skipped</p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button onClick={handlePreview} disabled={loading || (uploadMethod === 'file' && !file) || (uploadMethod === 'google' && !googleSheetUrl)}
                className="py-3 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-white font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="preview-btn">
                {loading ? <><Loader2 className="w-5 h-5 animate-spin" />Loading...</> : <><Eye className="w-5 h-5" />Preview First 100 Rows</>}
              </button>
              <button onClick={() => handleImport(true)} disabled={loading || (uploadMethod === 'file' && !file) || (uploadMethod === 'google' && !googleSheetUrl)}
                className="py-3 rounded-xl bg-amber-500 hover:bg-amber-600 text-black font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="direct-import-btn">
                {loading ? <><Loader2 className="w-5 h-5 animate-spin" />Starting...</> : <><Zap className="w-5 h-5" />Import Directly (No Preview)</>}
              </button>
            </div>

            {/* Template section */}
            <div className="border border-zinc-800 rounded-xl p-5 space-y-4">
              <h3 className="text-white font-medium flex items-center gap-2">
                <FileText className="w-5 h-5 text-amber-400" />Template & Column Guide
              </h3>
              <button onClick={() => window.open(`${API}/admin/bulk-import/template/csv`, '_blank')}
                className="flex items-center gap-2 text-amber-400 hover:text-amber-300 transition-colors text-sm" data-testid="download-template-btn">
                <Download className="w-4 h-4" />Download CSV Template
              </button>
              <div className="text-sm text-zinc-400 space-y-1">
                <p><strong className="text-zinc-300">Required:</strong> name, category</p>
                <p><strong className="text-zinc-300">Optional:</strong> description, phone, website, address, city, state, zip_code, image_url, social links</p>
                <p><strong className="text-zinc-300">Flexible columns:</strong> "Business Name", "name", "company" all map to name</p>
              </div>
              {template?.categories && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-zinc-300">Valid Categories:</span>
                    <button onClick={() => { navigator.clipboard.writeText(template.categories.map(c => c.id).join(', ')); toast.success('Copied'); }}
                      className="text-xs text-zinc-500 hover:text-zinc-300 flex items-center gap-1">
                      <Copy className="w-3 h-3" />Copy
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {template.categories.slice(0, 18).map(cat => (
                      <span key={cat.id} className="text-xs px-2 py-1 rounded bg-zinc-800 text-zinc-400">{cat.name}</span>
                    ))}
                    {template.categories.length > 18 && (
                      <span className="text-xs px-2 py-1 rounded bg-zinc-800 text-zinc-500">+{template.categories.length - 18} more</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* ── Preview Tab ── */}
          <TabsContent value="preview" className="space-y-6">
            {preview && (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {[
                    { label: 'Total Rows', value: preview.total_rows.toLocaleString(), color: 'text-white', bg: 'border-zinc-800' },
                    { label: 'Valid', value: preview.valid_count.toLocaleString(), color: 'text-emerald-400', bg: 'border-emerald-500/30' },
                    { label: 'Errors (sample)', value: preview.error_count.toLocaleString(), color: 'text-red-400', bg: 'border-red-500/30' },
                  ].map(({ label, value, color, bg }) => (
                    <div key={label} className={`p-4 rounded-xl bg-zinc-900 border ${bg} text-center`}>
                      <p className={`text-2xl font-bold ${color}`}>{value}</p>
                      <p className="text-sm text-zinc-500">{label}</p>
                    </div>
                  ))}
                </div>

                {preview.note && (
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-blue-500/10 border border-blue-500/30 text-xs text-blue-300">
                    <Info className="w-4 h-4 flex-shrink-0" />
                    {preview.note}
                  </div>
                )}

                <button onClick={() => handleImport(false)} disabled={loading || preview.valid_count === 0}
                  className="w-full py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="import-btn">
                  {loading ? <><Loader2 className="w-5 h-5 animate-spin" />Starting import...</> : <><Play className="w-5 h-5" />Start Import ({preview.total_rows.toLocaleString()} businesses)</>}
                </button>

                <div className="space-y-2 max-h-[500px] overflow-y-auto pr-2">
                  <h3 className="text-white font-medium text-sm">Preview — first {preview.rows.length} rows</h3>
                  {preview.rows.map((row, i) => <PreviewRow key={i} row={row} />)}
                </div>
              </>
            )}
          </TabsContent>

          {/* ── History Tab ── */}
          <TabsContent value="history" className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-white font-medium">Import History</h3>
              <div className="flex items-center gap-2">
                <GeocodeButton />
                <button onClick={fetchJobs} disabled={loadingJobs} className="p-2 rounded-lg hover:bg-zinc-800 text-zinc-400 hover:text-white transition-colors" data-testid="refresh-jobs-btn">
                  <RefreshCw className={`w-5 h-5 ${loadingJobs ? 'animate-spin' : ''}`} />
                </button>
              </div>
            </div>
            {loadingJobs && jobs.length === 0 ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-zinc-600" /></div>
            ) : jobs.length === 0 ? (
              <div className="text-center py-12 text-zinc-500">
                <Table className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No import jobs yet</p>
                <p className="text-sm">Upload a file to get started</p>
              </div>
            ) : (
              <div className="space-y-4">
                {jobs.map(job => (
                  <ImportJobCard key={job.job_id} job={job} onRefresh={fetchJobs} onDelete={handleJobDelete} />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
